import React, { useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useAdminTx } from "../../hooks/useContract";
import { useNotification } from "../../hooks/useNotification";
import { bytes32ToString, stringToBytes32, formatTimestamp, shortAddress, formatUSDT } from "../../utils/format";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";
import { PACKAGES, DEFAULT_REFERRER_ID } from "../../config/constants";

function AdminUsers() {
  const notify = useNotification();
  const { getContract, getLogic } = useWeb3();
  const { send, pending } = useAdminTx();

  // Unified search — accepts ID or wallet address
  const [lookupInput, setLookupInput] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupPackages, setLookupPackages] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Virtual register
  const [vrWallet, setVrWallet] = useState("");
  const [vrReferrer, setVrReferrer] = useState(String(DEFAULT_REFERRER_ID));
  const [vrName, setVrName] = useState("");
  const [vrUpTo, setVrUpTo] = useState(1);

  // Admin topup
  const [atUserId, setAtUserId] = useState("");
  const [atPackage, setAtPackage] = useState(1);

  // Update name
  const [unUserId, setUnUserId] = useState("");
  const [unName, setUnName] = useState("");

  // Update wallet
  const [uwUserId, setUwUserId] = useState("");
  const [uwWallet, setUwWallet] = useState("");

  const doLookup = async () => {
    if (!lookupInput) return;
    const contract = getContract();
    if (!contract) return;
    setLookupLoading(true);
    setLookupResult(null);
    setLookupPackages([]);
    try {
      let userId = null;

      // Auto-detect: address or numeric id
      if (ethers.utils.isAddress(lookupInput.trim())) {
        const id = await contract.addressToId(lookupInput.trim());
        userId = id.toString();
      } else if (/^\d+$/.test(lookupInput.trim())) {
        userId = lookupInput.trim();
      } else {
        notify.error("Enter a User ID or wallet address");
        setLookupLoading(false);
        return;
      }

      if (!userId || userId === "0") {
        notify.warning("User not found");
        setLookupLoading(false);
        return;
      }

      const raw = await contract.users(userId);
      if (raw.id.toString() === "0") {
        notify.warning("No user with that ID");
        setLookupLoading(false);
        return;
      }
      setLookupResult({
        id: raw.id.toString(),
        wallet: raw.wallet,
        name: bytes32ToString(raw.name),
        referrerId: raw.referrerId.toString(),
        joinTime: raw.joinTime.toNumber(),
        isBlocked: raw.isBlocked,
        serialNumber: raw.serialNumber.toString(),
        totalDirectReferrals: raw.totalDirectReferrals.toString(),
        totalClaimed: raw.totalClaimed,
        totalIncome: raw.totalIncome,
      });

      // Fetch packages
      const pkgs = await Promise.all(
        PACKAGES.map(async (p) => {
          const raw2 = await contract.userPackages(userId, p.id);
          return {
            ...p,
            isActive: raw2.isActive,
            ceilingUpgraded: raw2.ceilingUpgraded,
            totalInvested: raw2.totalInvested,
            totalEarned: raw2.totalEarned,
            ceilingLimit: raw2.ceilingLimit,
          };
        })
      );
      setLookupPackages(pkgs);
    } catch (e) {
      notify.error("Lookup failed");
    } finally {
      setLookupLoading(false);
    }
  };

  const copyText = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      notify.success(label);
    } catch {
      notify.error("Could not copy");
    }
  };

  // block/unblock/virtualRegister/adminTopup now live on the LOGIC contract.
  // NOTE: Block flow is kept in the code (contract-side) but the UI button is
  // hidden per owner request. Unblock stays visible so a blocked user can be restored.
  const unblock = async (userId) => {
    const logic = getLogic();
    if (!logic) return notify.error("Logic contract not configured");
    const ok = await send(() => logic.unblockUser(userId), {
      pendingMsg: "Unblocking user...",
      successMsg: "User unblocked",
      action: "unblock_user",
      params: { userId: String(userId) },
    });
    if (ok) doLookup();
  };

  const doVirtualRegister = async () => {
    if (!ethers.utils.isAddress(vrWallet)) return notify.error("Invalid wallet address");
    if (!vrName.trim()) return notify.error("Name required");
    if (!vrReferrer) return notify.error("Referrer ID required");
    const logic = getLogic();
    if (!logic) return notify.error("Logic contract not configured");
    let nameBytes;
    try { nameBytes = stringToBytes32(vrName); }
    catch (nameE) { return notify.error(nameE.message); }
    const ok = await send(
      () => logic.adminRegister(vrWallet, vrReferrer, nameBytes, vrUpTo),
      {
        pendingMsg: "Registering virtually...",
        successMsg: "Virtual user created",
        action: "virtual_register",
        params: { wallet: vrWallet, referrerId: String(vrReferrer), name: vrName.trim(), upToPackage: vrUpTo },
      }
    );
    if (ok) { setVrWallet(""); setVrName(""); }
  };

  const doAdminTopup = async () => {
    if (!atUserId) return notify.error("User ID required");
    const logic = getLogic();
    if (!logic) return notify.error("Logic contract not configured");
    const ok = await send(
      () => logic.adminRetopupPackage(atUserId, atPackage),
      {
        pendingMsg: "Topping up package...",
        successMsg: "Package topped up",
        action: "admin_topup",
        params: { userId: String(atUserId), packageId: String(atPackage) },
      }
    );
    if (ok) setAtUserId("");
  };

  const doUpdateName = async () => {
    if (!unUserId || !unName.trim()) return notify.error("User ID + name required");
    const contract = getContract();
    if (!contract) return;
    let nameBytes;
    try { nameBytes = stringToBytes32(unName); }
    catch (nameE) { return notify.error(nameE.message); }
    const ok = await send(
      () => contract.updateUserName(unUserId, nameBytes),
      {
        pendingMsg: "Updating name...",
        successMsg: "Name updated",
        action: "update_name",
        params: { userId: String(unUserId), name: unName.trim() },
      }
    );
    if (ok) { setUnUserId(""); setUnName(""); }
  };

  const doUpdateWallet = async () => {
    if (!uwUserId) return notify.error("User ID required");
    if (!ethers.utils.isAddress(uwWallet)) return notify.error("Invalid wallet");
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.updateUserWallet(uwUserId, uwWallet),
      {
        pendingMsg: "Updating wallet...",
        successMsg: "Wallet updated",
        action: "update_wallet",
        params: { userId: String(uwUserId), newWallet: uwWallet },
      }
    );
    if (ok) { setUwUserId(""); setUwWallet(""); }
  };

  return (
    <div>
      {/* ============ LOOKUP ============ */}
      <div className="section-header">
        <div className="section-title">User Lookup</div>
      </div>
      <Card className="mb-lg">
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            className="input"
            placeholder="User ID or 0x wallet address"
            value={lookupInput}
            onChange={(e) => setLookupInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doLookup(); }}
          />
          <Button variant="secondary" size="sm" onClick={doLookup} loading={lookupLoading}>
            Find
          </Button>
        </div>

        {lookupResult && (
          <div style={{ marginTop: 16, padding: 14, background: "#0a0e24", borderRadius: 12, border: "1px solid var(--cyan-border)" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: "linear-gradient(135deg, var(--cyan), var(--violet))",
                color: "white", fontFamily: "var(--font-display)", fontSize: 18,
                fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid var(--cyan)",
              }}>
                {lookupResult.name ? lookupResult.name[0]?.toUpperCase() : "◇"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700 }}>
                  {lookupResult.name || "—"}
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--cyan)" }}>
                  #{lookupResult.id} · SN {lookupResult.serialNumber}
                </div>
              </div>
              <span className={`badge ${lookupResult.isBlocked ? "badge-red" : "badge-green"}`}>
                {lookupResult.isBlocked ? "BLOCKED" : "ACTIVE"}
              </span>
            </div>

            {/* Wallet with copy */}
            <div style={{ marginBottom: 10 }}>
              <div className="stat-card-label" style={{ marginBottom: 4 }}>Wallet</div>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "#050815", borderRadius: 8, padding: "8px 10px",
                border: "1px solid var(--border-strong)",
              }}>
                <div style={{
                  flex: 1, fontFamily: "var(--font-mono)", fontSize: 11,
                  color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {lookupResult.wallet}
                </div>
                <button
                  onClick={() => copyText(lookupResult.wallet, "Address copied")}
                  style={{
                    background: "var(--cyan)", color: "#000", border: "none",
                    padding: "4px 10px", borderRadius: 5, fontSize: 10,
                    fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-body)",
                    letterSpacing: "0.03em",
                  }}
                >
                  COPY
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <div className="stat-card-label" style={{ marginBottom: 3 }}>Referrer</div>
                <div className="mono" style={{ fontSize: 12, color: "var(--cyan)" }}>#{lookupResult.referrerId}</div>
              </div>
              <div>
                <div className="stat-card-label" style={{ marginBottom: 3 }}>Directs</div>
                <div className="mono" style={{ fontSize: 12 }}>{lookupResult.totalDirectReferrals}</div>
              </div>
              <div>
                <div className="stat-card-label" style={{ marginBottom: 3 }}>Total Income</div>
                <div className="mono" style={{ fontSize: 12, color: "var(--success)" }}>${formatUSDT(lookupResult.totalIncome)}</div>
              </div>
              <div>
                <div className="stat-card-label" style={{ marginBottom: 3 }}>Total Claimed</div>
                <div className="mono" style={{ fontSize: 12 }}>${formatUSDT(lookupResult.totalClaimed)}</div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <div className="stat-card-label" style={{ marginBottom: 3 }}>Joined</div>
                <div style={{ fontSize: 12 }}>{formatTimestamp(lookupResult.joinTime)}</div>
              </div>
            </div>

            {/* User's packages */}
            <div className="stat-card-label" style={{ marginTop: 10, marginBottom: 6 }}>Packages</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 12 }}>
              {lookupPackages.map((p) => {
                const invested = parseFloat(ethers.utils.formatUnits(p.totalInvested || 0, 18));
                const earned = parseFloat(ethers.utils.formatUnits(p.totalEarned || 0, 18));
                return (
                  <div
                    key={p.id}
                    style={{
                      padding: 6,
                      borderRadius: 6,
                      border: `1px solid ${p.isActive ? "var(--cyan-border)" : "var(--border)"}`,
                      background: p.isActive ? "rgba(0, 229, 255, 0.06)" : "#050815",
                      textAlign: "center",
                    }}
                    title={p.isActive ? `Invested $${invested.toFixed(0)} · Earned $${earned.toFixed(2)}` : "Locked"}
                  >
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 700, color: p.isActive ? "var(--cyan)" : "var(--text-muted)" }}>
                      P{p.id}
                    </div>
                    <div style={{ fontSize: 10, color: p.isActive ? "var(--text-secondary)" : "var(--text-dim)" }}>
                      ${p.price}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Unblock only — Block button hidden per owner request */}
            {lookupResult.isBlocked && (
              <Button variant="secondary" full size="sm" loading={pending} onClick={() => unblock(lookupResult.id)}>
                ✓ Unblock
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* ============ VIRTUAL REGISTER ============ */}
      <div className="section-header">
        <div className="section-title">Virtual Register</div>
      </div>
      <Card className="mb-lg">
        <p className="text-muted mb-md" style={{ fontSize: 12 }}>
          Register a wallet without on-chain payment. Unlimited ceiling.
        </p>
        <div className="input-group">
          <label className="input-label">Wallet</label>
          <input className="input" placeholder="0x..." value={vrWallet} onChange={(e) => setVrWallet(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">Referrer ID</label>
          <input type="number" className="input" value={vrReferrer} onChange={(e) => setVrReferrer(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">Name</label>
          <input className="input" value={vrName} onChange={(e) => setVrName(e.target.value)} maxLength={31} />
        </div>
        <div className="input-group">
          <label className="input-label">Activate up to</label>
          <select className="select" value={vrUpTo} onChange={(e) => setVrUpTo(Number(e.target.value))}>
            {PACKAGES.map((p) => (
              <option key={p.id} value={p.id}>Package {p.id} (${p.price})</option>
            ))}
          </select>
        </div>
        <Button variant="primary" full loading={pending} onClick={doVirtualRegister}>
          Create Virtual User
        </Button>
      </Card>

      {/* ============ ADMIN TOPUP ============ */}
      <div className="section-header">
        <div className="section-title">Admin Topup</div>
      </div>
      <Card className="mb-lg">
        <p className="text-muted mb-md" style={{ fontSize: 12 }}>
          Re-topup a user's package without on-chain payment. Extends their ceiling.
        </p>
        <div className="input-group">
          <label className="input-label">User ID</label>
          <input type="number" className="input" value={atUserId} onChange={(e) => setAtUserId(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">Package</label>
          <select className="select" value={atPackage} onChange={(e) => setAtPackage(Number(e.target.value))}>
            {PACKAGES.map((p) => (
              <option key={p.id} value={p.id}>Package {p.id} (${p.price})</option>
            ))}
          </select>
        </div>
        <Button variant="primary" full loading={pending} onClick={doAdminTopup}>
          Topup Package
        </Button>
      </Card>

      {/* ============ UPDATE NAME ============ */}
      <div className="section-header">
        <div className="section-title">Update Name</div>
      </div>
      <Card className="mb-lg">
        <div className="input-group">
          <label className="input-label">User ID</label>
          <input type="number" className="input" value={unUserId} onChange={(e) => setUnUserId(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">New Name</label>
          <input className="input" value={unName} onChange={(e) => setUnName(e.target.value)} maxLength={31} />
        </div>
        <Button variant="secondary" full loading={pending} onClick={doUpdateName}>Update Name</Button>
      </Card>

      {/* ============ UPDATE WALLET ============ */}
      <div className="section-header">
        <div className="section-title">Update Wallet</div>
      </div>
      <Card className="mb-lg">
        <div className="input-group">
          <label className="input-label">User ID</label>
          <input type="number" className="input" value={uwUserId} onChange={(e) => setUwUserId(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">New Wallet</label>
          <input className="input" placeholder="0x..." value={uwWallet} onChange={(e) => setUwWallet(e.target.value)} />
        </div>
        <Button variant="secondary" full loading={pending} onClick={doUpdateWallet}>Update Wallet</Button>
      </Card>
    </div>
  );
}

export default AdminUsers;
